import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

import {
  classifyFlowgateResultError,
  useFlowgateResultSchema,
} from '@/api/flowgate';
import PageHeader from '@/components/PageHeader/PageHeader';
import FlowgateResultGrid, { ExportMenu } from './FlowgateResultGrid';
import FlowgateReportStep from './FlowgateReportStep';

// Mirrors the legacy ResultPage flow (Prioritization & Filtration → User 1 →
// User 2 → Report). The final report step is a stub: Flowgate has no report
// endpoint yet (it shows the current validation summary instead).
const FLOWGATE_STEPS = [
  'Prioritization & Filtration',
  'First Validation',
  'Second Validation',
  'Generate Report',
] as const;

const FlowgateResultPage = () => {
  const { experimentId, resultId } = useParams<{
    experimentId: string;
    resultId: string;
  }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const {
    data: schema,
    isLoading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema,
  } = useFlowgateResultSchema(resultId);

  const handleNext = () =>
    setActiveStep((s) => Math.min(s + 1, FLOWGATE_STEPS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const backToResults = () =>
    experimentId
      ? navigate(`/result/experiment/${experimentId}`)
      : navigate(-1);

  const renderStep = () => {
    if (!schema || !resultId) return null;
    switch (activeStep) {
      case 0:
        return (
          <FlowgateResultGrid
            resultId={resultId}
            schema={schema}
            mode="select"
          />
        );
      case 1:
        return (
          <FlowgateResultGrid
            resultId={resultId}
            schema={schema}
            mode="first_validation"
          />
        );
      case 2:
        return (
          <FlowgateResultGrid
            resultId={resultId}
            schema={schema}
            mode="second_validation"
          />
        );
      case 3:
        return <FlowgateReportStep resultId={resultId} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{ width: '100%', maxWidth: 1680, mx: 'auto', p: { xs: 2, md: 3 } }}
    >
      <PageHeader
        eyebrow="Variant interpretation"
        title={schema?.name ?? 'Result interpretation'}
        description="Prioritize variants, complete the validation workflow, and prepare a clinical report."
        actions={
          <>
            <Button
              startIcon={<ArrowBack />}
              onClick={backToResults}
              variant="outlined"
              size="small"
            >
              Back to results
            </Button>
            {resultId && <ExportMenu resultId={resultId} />}
          </>
        }
      />

      {schema && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h3">Result summary</Typography>
              <Typography variant="body2" color="text.secondary">
                Use the workflow stages below to progress from prioritization to
                reporting.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {schema.row_count != null && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${schema.row_count.toLocaleString()} total rows`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`${schema.columns.length} columns`}
              />
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: 'block' }}
          >
            Result ID · {resultId}
          </Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={1.5}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            disabled={activeStep === 0}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
          <Box sx={{ flex: 1, overflowX: 'auto', py: 0.5 }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{ minWidth: 680 }}
            >
              {FLOWGATE_STEPS.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    onClick={() => setActiveStep(index)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Button
            endIcon={<ArrowForward />}
            onClick={handleNext}
            disabled={activeStep === FLOWGATE_STEPS.length - 1}
            variant="contained"
            size="small"
          >
            Next
          </Button>
        </Stack>
      </Paper>

      {/* Step content */}
      {schemaLoading && (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {schemaError &&
        (() => {
          const info = classifyFlowgateResultError(schemaError);
          return (
            <Alert
              severity={info.kind === 'not_ready' ? 'warning' : 'error'}
              action={
                info.kind === 'not_ready' || info.kind === 'unavailable' ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => refetchSchema()}
                  >
                    Retry
                  </Button>
                ) : null
              }
            >
              {info.message}
            </Alert>
          );
        })()}

      {!schemaLoading && !schemaError && renderStep()}
    </Box>
  );
};

export default FlowgateResultPage;
